'use client';
/**
 * components/NavPixels.tsx — trame de points scintillants (identité « pixel
 * grid »). Extrait de l'ancien Navbar.tsx desktop (supprimé le 21/09, plus
 * aucune trace de nav desktop dans le code) : ce composant n'a rien de
 * spécifique à la navigation, il sert de fond à OttoAssistant/OttoChat/tabs.
 */
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { themeStore, valeurCss } from './theme';

// overlay=false désactive le voile dégradé (spécifique navbar) ; radius ajuste l'arrondi.
export function NavPixels({ color, overlay = true, radius = 40 }: {
  color: string; overlay?: boolean;
  /** Arrondi du canvas. Accepte une valeur CSS (`var(--goo-r)`) : le panneau
   *  qui l'héberge publie son rayon effectif, la trame le suit sans le redire. */
  radius?: number | string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  // Un canvas ne sait pas ce qu'est `var(--otto-pixel)` : on résout la variable
  // nous-mêmes, et on s'abonne au thème pour que le dessin suive la bascule.
  // S'abonner plutôt que recopier dans un état évite le rendu en cascade.
  const theme = useSyncExternalStore(themeStore.subscribe, themeStore.lire, themeStore.serveur);
  const teinte = useMemo(
    () => valeurCss(color, theme === 'dark' ? '#4FA578' : '#FFFFFF'),
    [color, theme],
  );
  const glow = theme === 'dark';
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const gap = 4, dotSize = 2;
    // Deux réglages, et ce sont eux qui font le rythme :
    //  `spd`  — vitesse de la montée/descente d'un point (facteur par image
    //           `spd * 16`, donc ~0,13 à 0,32 : un point s'allume ou s'éteint en
    //           3 à 8 images, contre 10 à 30 auparavant) ;
    //  `next` — le temps qu'il reste dans son état, 0,5 à 2 s au lieu de 3 à 10.
    // Ensemble : une trame qui scintille, et non une respiration lente.
    const newDot = () => ({ o: 0, t: 0, spd: .008 + Math.random() * .012, next: Math.floor(Math.random() * 90), frame: 0 });
    let cols = 0, rows = 0;
    let dots: ReturnType<typeof newDot>[] = [];
    /**
     * La TRAME se recalcule avec le canvas. Elle ne l'était qu'une fois, au
     * montage : après une rotation d'écran le canvas grandissait mais la grille
     * gardait ses anciennes dimensions — on ne voyait plus scintiller que le
     * tiers supérieur du panneau, le reste étant hors de la boucle.
     *
     * `setTransform` et non `scale` : `scale` MULTIPLIE la matrice courante, si
     * bien que deux redimensionnements de suite laissaient le contexte à dpr²
     * et tout le dessin débordait du cadre.
     */
    const resize = () => {
      const r = cv.parentElement!.getBoundingClientRect();
      if (!r.width || !r.height) return;
      cv.width = r.width * dpr; cv.height = r.height * dpr;
      cv.style.width = r.width + 'px'; cv.style.height = r.height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const nc = Math.ceil(r.width / gap) + 1, nr = Math.ceil(r.height / gap) + 1;
      if (nc === cols && nr === rows) return;
      cols = nc; rows = nr;
      // Les points déjà allumés gardent leur phase : un redimensionnement ne
      // doit pas rallumer toute la trame d'un coup.
      const prev = dots;
      dots = Array.from({ length: cols * rows }, (_, i) => prev[i] ?? newDot());
    };
    resize();
    let raf: number;
    const draw = () => {
      const w = cv.width / dpr, h = cv.height / dpr;
      ctx.clearRect(0, 0, w, h);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const d = dots[r * cols + c];
          d.frame++;
          if (d.frame >= d.next) { d.t = Math.random() < .12 ? (.4 + Math.random() * .5) : 0; d.next = d.frame + 30 + Math.floor(Math.random() * 90); }
          d.o += (d.t - d.o) * d.spd * 16;
          if (d.o > .03) {
            // Halo au-delà de 55 % de luminosité, DE NUIT SEULEMENT : une lueur
            // se lit sur du sombre, elle n'existe pas sur de l'ivoire — là elle
            // ne fait qu'empâter le point.
            // `shadowBlur` est coûteux : on ne le paie QUE sur les points
            // vraiment allumés (une poignée à chaque image), et on le remet à
            // zéro juste après — sinon il s'applique à tous les suivants, y
            // compris ceux qui s'éteignent. Le rayon suit la luminosité : un
            // point qui monte voit son halo grandir avec lui, ce qui donne
            // l'allumage plutôt que la présence.
            const hot = glow && d.o > .55 ? (d.o - .55) / .45 : 0;
            if (hot) { ctx.shadowColor = teinte; ctx.shadowBlur = 1.5 + hot * 4.5; }
            ctx.fillStyle = teinte; ctx.globalAlpha = d.o;
            ctx.fillRect(c * gap, r * gap, dotSize, dotSize);
            // Un second passage sur le cœur du point : le halo seul délave la
            // lueur, ce noyau lui rend son piqué.
            if (hot) { ctx.globalAlpha = Math.min(1, d.o + hot * .35); ctx.fillRect(c * gap, r * gap, dotSize, dotSize); ctx.shadowBlur = 0; }
          }
        }
      }
      ctx.globalAlpha = 1;
      if (overlay) {
        const grd = ctx.createLinearGradient(0, 0, w, 0);
        grd.addColorStop(0, 'rgba(10,5,2,.85)'); grd.addColorStop(0.25, 'rgba(10,5,2,.6)');
        grd.addColorStop(0.5, 'rgba(10,5,2,.35)'); grd.addColorStop(1, 'rgba(10,5,2,.1)');
        ctx.fillStyle = grd; ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = 'rgba(10,5,2,.25)'; ctx.fillRect(0, 0, w, h);
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    // ResizeObserver plutôt que `resize` seul : le panneau du chat change de
    // taille sans que la fenêtre bouge (croissance du popover), et la rotation
    // iOS publie ses nouvelles dimensions APRÈS l'événement `resize`.
    const ro = new ResizeObserver(resize);
    ro.observe(cv.parentElement!);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [teinte, overlay, glow]);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, borderRadius: typeof radius === 'number' ? `${radius}px` : radius, overflow: 'hidden' }} />;
}
